import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import {NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {getConfigByApp} from '@utils';
import {metaInfo} from '@configs';
import {HOME, SITEMAP} from '@constants';
import loadSEO from '@helpers/loadSEO';
import {getSearchBarVariant} from '@helpers';
import {wrapper} from '@jobseeker/common-core/stores';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import {useDispatch} from 'react-redux';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {tagService} from '@jobseeker/common-core/services';
import {locations} from '@jobseeker/lotus/components/JobSearchBar/data';
import type {GetServerSidePropsContext} from 'next/types';
import {Box, Grid, Typography} from '@mui/material';
import {Link} from '@jobseeker/lotus/elements';

type SitemapProps = {
  seoConfig: object;
  metaData: object;
  tagName: string;
  urls: {url: string; value: string}[];
  canonicalUrl: string;
};

const TagAndLocation = ({
  seoConfig,
  metaData,
  tagName,
  urls,
  canonicalUrl,
}: SitemapProps) => {
  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  useValidateAuth(dispatch);
  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
  const breadcrumbElements = [
    {
      name: 'Home',
      item: '/',
    },
    {
      name: 'Sitemap',
      item: `/sitemap`,
    },
    {
      name: `Top location for ${tagName} Jobs`,
      item: '',
    },
  ];

  return (
    <Interceptor metaInfo={metaData}>
      <SeoLayout
        seoConfig={{
          ...seoConfig,
          canonical: `${
            (seoConfig as {canonical?: string}).canonical
          }${canonicalUrl.replace('/sitemap', '')}`,
        }}
      />
      <NavLayout>
        <ResponsiveLayout
          breadcrumbElements={breadcrumbElements}
          variant={VersionVariants.VERSION_TWO}
        >
          <Box
            sx={{
              mx: isAppIimjobs ? 0 : 2,
            }}
          >
            <Typography component="h2" mb={2} mt={isAppIimjobs ? 0 : 6}>
              Top Locations for {tagName} Jobs
            </Typography>
            <Grid container>
              {urls.map((url, index) => (
                <Grid item xs={4} key={index}>
                  <Link href={url.url} target="_blank">
                    {url.value}
                  </Link>
                </Grid>
              ))}
            </Grid>
          </Box>
        </ResponsiveLayout>
      </NavLayout>
    </Interceptor>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const searchBarVariant = await getSearchBarVariant({req: context.req});
    store.dispatch({
      type: 'AppContext/set_ab_variants',
      payload: {searchBar: searchBarVariant},
    });
    const canonicalUrl = context?.resolvedUrl;
    const {slug = ''}: {slug: string} = context.query;
    let tagId = 0;

    try {
      tagId = parseInt(
        slug
          .replace(/\.html/, '')
          .split('-')
          .pop() ?? '0',
        10
      );
    } catch (err) {
      tagId = 0;
    }

    const metaData = getConfigByApp(metaInfo, HOME, true, SITEMAP);

    const seoConfig = await loadSEO(SITEMAP);

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: SITEMAP},
    });

    const baseUrl = process.env.NEXT_PUBLIC_LOAD_CONFIG;

    const {tagName, tagSlug}: {id: number; tagName: string; tagSlug: string} =
      (await tagService.getTags(`${tagId}`)) ?? {};

    const urls: {url: string; value: string}[] = [];

    locations?.forEach(({label}) => {
      const url = new URL(`/k/${tagSlug}-jobs?loc=${label}`, baseUrl).href;

      urls.push({
        url,
        value: label,
      });
    });

    return {
      props: {
        seoConfig,
        metaData,
        tagName,
        urls,
        canonicalUrl,
      },
    };
  }
);

export default TagAndLocation;
